import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { Coins } from "lucide-react";
import { toast } from "sonner";

// A button that fetches a quote, shows a confirm dialog, then runs the paid action.
export function QuoteButton({ assignmentId, action, label, icon: Icon, onDone, testid, disabled }) {
  const { refreshBalance } = useAuth();
  const [quote, setQuote] = useState(null);
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);

  const start = async () => {
    try {
      const { data } = await api.get(`/assignments/${assignmentId}/quote`, { params: { action } });
      setQuote(data);
      setOpen(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const confirm = async () => {
    setRunning(true);
    try {
      const endpoint = action === "blueprint" ? "blueprint" : "evaluate";
      const { data } = await api.post(`/assignments/${assignmentId}/${endpoint}`);
      refreshBalance();
      setOpen(false);
      onDone && onDone(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      refreshBalance();
      setOpen(false);
    } finally { setRunning(false); }
  };

  return (
    <>
      <Button onClick={start} disabled={disabled} data-testid={testid} className="bg-[#0B5D3B] text-white hover:bg-[#08472D]">
        {Icon && <Icon className="mr-2 h-4 w-4" />}{label}
      </Button>
      <AlertDialog open={open} onOpenChange={(v) => !running && setOpen(v)}>
        <AlertDialogContent data-testid="quote-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Coins className="h-5 w-5 text-[#D97706]" /> Confirm {quote?.action}</AlertDialogTitle>
            <AlertDialogDescription>
              This will use <strong className="text-foreground">{quote?.tokens} tokens</strong>. You have {quote?.balance}.
              {quote && !quote.can_afford && <span className="mt-2 block font-medium text-red-600">You don't have enough tokens. <a href="/app/billing" className="underline" data-testid="quote-buy-link">Buy more tokens</a>.</span>}
              {running && <span className="mt-3 block text-foreground">Working on it… this can take up to a minute. It's safe to wait here.</span>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={running} data-testid="quote-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirm(); }} disabled={running || (quote && !quote.can_afford)}
              data-testid="quote-confirm" className="bg-[#0B5D3B] text-white hover:bg-[#08472D]">
              {running ? "Working…" : `Use ${quote?.tokens} tokens`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
